import { useState } from "react";
import type { SaleDTO, SalesDTO } from "@easyshop/shared";
import { Empty, ErrorState, Loading, cx } from "@/components/ui";
import { SaleDetail } from "@/components/sales/SaleRow";
import { IosGroup, IosSection, MobileStat, MoreButton } from "@/components/mobile/MobileUi";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { usePeriod } from "@/hooks/usePeriod";
import { useBoutiqueSales } from "@/lib/queries";
import { dayMonth, eur, eur2, num } from "@/lib/format";
import { MobilePeriodSwitcher } from "./Home";

/** Totaux de la période (toutes pages) en grille 2 × 2. */
function SalesKpis({ data }: { data: SalesDTO }) {
  const t = data.totals;
  return (
    <div className="grid grid-cols-2 gap-[10px]">
      <MobileStat label="Tickets" value={num(data.total)} />
      <MobileStat label="CA encaissé" value={eur(t.amount)} />
      <MobileStat
        label="Remises accordées"
        value={eur(t.discount)}
        valueClassName={t.discount > 0 ? "text-warn" : undefined}
        hint={`${num(t.discountedSales)} ticket${t.discountedSales > 1 ? "s" : ""} remisé${t.discountedSales > 1 ? "s" : ""}`}
      />
      <MobileStat label="Bénéfice" value={eur(t.profit)} valueClassName={t.profit != null && t.profit < 0 ? "text-danger" : undefined} />
    </div>
  );
}

/** Ticket résumé ; le détail (produits, remises par ligne, paiement) se déplie au toucher. */
function TicketRow({ s, showDay, open, onToggle }: { s: SaleDTO; showDay: boolean; open: boolean; onToggle: () => void }) {
  const names = s.lines.map((l) => (l.quantity > 1 ? `${l.name} ×${num(l.quantity)}` : l.name)).join(", ");
  const discounted = s.discount > 0.005;
  return (
    <div className="ios-sep">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left">
        <span className="w-11 flex-shrink-0 text-[15px] tabular text-muted">
          {showDay && <span className="block text-[12px] text-faint">{dayMonth(s.at)}</span>}
          {s.time}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-[6px] text-[16px]">
            <span className="truncate">
              {num(s.items)} article{s.items > 1 ? "s" : ""}
            </span>
            {discounted && <span className="flex-shrink-0 rounded-[6px] bg-warn-bg px-[6px] py-px text-[12px] font-semibold text-warn">−{eur2(s.discount)}</span>}
          </span>
          {names && <span className="block truncate text-[13px] text-muted">{names}</span>}
        </span>
        <span className="flex-shrink-0 text-[16px] font-semibold tabular">{eur2(s.amount)}</span>
        <span aria-hidden className={cx("flex-shrink-0 text-[13px] text-ios-chevron transition-transform", open && "rotate-180")}>
          ⌄
        </span>
      </button>
      {open && <SaleDetail s={s} className="mx-4 mb-3 rounded-[10px] bg-[oklch(0.97_0.004_145)] px-3 py-[10px]" />}
    </div>
  );
}

/** Une page de tickets (lignes du groupe « Tickets »). */
function SalesChunk({ boutiqueId, page, openId, onToggle }: { boutiqueId: string; page: number; openId: string | null; onToggle: (id: string) => void }) {
  const q = useBoutiqueSales(boutiqueId, page);
  const { period } = usePeriod();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  return (
    <>
      {q.data!.sales.map((s) => (
        <TicketRow key={s.id} s={s} showDay={period !== "jour"} open={openId === s.id} onToggle={() => onToggle(s.id)} />
      ))}
    </>
  );
}

/** Ventes mobile : totaux de la période et tickets chronologiques de la boutique choisie. */
export function MobileSales() {
  const { selectedId } = useSelectedBoutique();
  const { period, cmp, date, range } = usePeriod();
  return (
    <>
      <MobilePeriodSwitcher />
      {selectedId && <SalesList key={`${selectedId}-${period}-${cmp}-${date}-${range?.from}-${range?.to}`} boutiqueId={selectedId} />}
    </>
  );
}

function SalesList({ boutiqueId }: { boutiqueId: string }) {
  const [pages, setPages] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const first = useBoutiqueSales(boutiqueId, 1);
  // Même requête que la dernière page affichée (déjà en cache) : y a-t-il une page suivante ?
  const last = useBoutiqueSales(boutiqueId, pages);
  const toggle = (id: string) => setOpenId((o) => (o === id ? null : id));
  if (first.isLoading) return <Loading />;
  if (first.error) return <ErrorState error={first.error} />;
  if (!first.data || first.data.sales.length === 0) return <Empty>Aucune vente sur la période.</Empty>;
  return (
    <>
      <SalesKpis data={first.data} />
      <IosSection title="Tickets">
        <IosGroup>
          {Array.from({ length: pages }, (_, i) => (
            <SalesChunk key={i} boutiqueId={boutiqueId} page={i + 1} openId={openId} onToggle={toggle} />
          ))}
        </IosGroup>
      </IosSection>
      {last.data && last.data.page < last.data.pages && <MoreButton onClick={() => setPages((p) => p + 1)}>Voir plus</MoreButton>}
    </>
  );
}
